import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds/site";

const links = [
  { id: "about", label: "About", testid: NAV.about },
  { id: "services", label: "Services", testid: NAV.services },
  { id: "how-it-works", label: "How It Works", testid: NAV.how },
  { id: "booking", label: "Booking", testid: NAV.booking },
  { id: "aftercare", label: "Aftercare", testid: "nav-link-aftercare" },
  { id: "terms", label: "Warranty", testid: "nav-link-warranty" },
  { id: "contact", label: "Contact", testid: NAV.contact },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl shadow-lg shadow-emerald-950/10 border-b border-emerald-100"
          : "bg-transparent"
      }`}
    >
      {/* moving shimmer line along the bottom edge */}
      <div className="absolute bottom-0 inset-x-0 h-px overflow-hidden pointer-events-none">
        <div className="line-sweep h-full w-1/3" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <nav
          className={`flex items-center justify-between transition-all duration-500 ${
            scrolled ? "py-3" : "py-5 md:py-6"
          }`}
        >
          {/* Brand */}
          <button
            data-testid={NAV.logo}
            onClick={() => scrollTo("hero")}
            className="flex items-center gap-4 md:gap-5 group text-left"
          >
            <div className="relative logo-float">
              <div
                className={`absolute -inset-2 rounded-2xl blur-lg -z-10 logo-glow ${
                  scrolled ? "bg-emerald-500/30" : "bg-emerald-300/40"
                }`}
              />
              <div className="w-14 h-14 md:w-[68px] md:h-[68px] rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-800 flex items-center justify-center overflow-hidden shadow-xl shadow-emerald-900/40 ring-1 ring-white/40">
                <img
                  src="/j7-logo.png"
                  alt="J7 Collective"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="font-display tracking-tight leading-none">
              <div
                data-testid="nav-company-name"
                className={`font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-[42px] leading-none ${
                  scrolled ? "name-sheen-dark" : "name-sheen"
                }`}
              >
                J7 Collective
                <span
                  className={`ml-2 align-middle text-base sm:text-lg md:text-xl font-semibold ${
                    scrolled ? "text-emerald-500" : "text-emerald-200/90"
                  }`}
                  style={{ WebkitTextFillColor: "currentColor" }}
                >
                  Ltd
                </span>
              </div>

              <div className="flex items-center gap-2 mt-1.5 md:mt-2">
                {/* beading droplet dots — moving parts */}
                <span className="flex items-center gap-1">
                  <span
                    className={`bead-dot w-1.5 h-1.5 rounded-full ${
                      scrolled ? "bg-emerald-600" : "bg-emerald-200"
                    }`}
                  />
                  <span
                    className={`bead-dot b2 w-1.5 h-1.5 rounded-full ${
                      scrolled ? "bg-emerald-600" : "bg-emerald-200"
                    }`}
                  />
                  <span
                    className={`bead-dot b3 w-1.5 h-1.5 rounded-full ${
                      scrolled ? "bg-emerald-600" : "bg-emerald-200"
                    }`}
                  />
                </span>
                <span
                  className={`text-[10px] sm:text-[11px] md:text-xs font-bold tracking-[0.22em] uppercase ${
                    scrolled ? "text-emerald-700" : "text-emerald-100"
                  }`}
                >
                  Glass Surface Protection Specialists
                </span>
              </div>
            </div>
          </button>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={l.testid}
                onClick={() => scrollTo(l.id)}
                className={`relative px-3.5 py-2 text-sm font-semibold transition-colors group/link ${
                  scrolled
                    ? "text-emerald-950 hover:text-emerald-600"
                    : "text-white/90 hover:text-white"
                }`}
              >
                {l.label}
                <span
                  className={`absolute left-3.5 right-3.5 -bottom-0.5 h-0.5 rounded-full origin-left scale-x-0 group-hover/link:scale-x-100 transition-transform duration-300 ${
                    scrolled ? "bg-emerald-500" : "bg-emerald-200"
                  }`}
                />
              </button>
            ))}
            <button
              data-testid={NAV.ctaBook}
              onClick={() => scrollTo("booking")}
              className={`ml-3 px-5 py-2.5 rounded-full text-sm font-semibold transition-all shadow-lg ${
                scrolled
                  ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-600/25"
                  : "bg-white text-emerald-950 hover:bg-emerald-50 shadow-emerald-950/20"
              }`}
            >
              Book a Visit
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid={NAV.mobileToggle}
            className={`lg:hidden w-11 h-11 rounded-full flex items-center justify-center transition-colors ${
              scrolled ? "bg-emerald-600 text-white" : "bg-white/20 backdrop-blur-md text-white border border-white/30"
            }`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </nav>

        {open && (
          <div className="lg:hidden mb-4 glass rounded-3xl p-4 space-y-1 shadow-xl">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                className="w-full text-left px-4 py-3 rounded-2xl text-slate-800 font-medium hover:bg-emerald-50"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo("booking")}
              className="w-full mt-2 px-4 py-3 rounded-2xl bg-emerald-600 text-white font-semibold"
            >
              Book a Visit
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
